/**
 * Evidencia de la última entrega de la herramienta (CR-03).
 *
 * Lo que quedó de la asignación más reciente: en qué estado se entregó, la nota
 * de quien la entregó, las fotos de ese momento y la firma de quien la recibió.
 * Hasta ahora la firma se guardaba al aceptar desde la app y no había ninguna
 * pantalla donde se pudiera ver.
 *
 * Va arriba de «Última evidencia», que es otra cosa: esa muestra las fotos del
 * movimiento más reciente sea cual sea —una devolución, un mantenimiento—,
 * mientras que esta es siempre la entrega.
 *
 * Lo que pasó al aceptarla —quién, cuándo, desde qué IP, qué términos y la
 * firma— vive en `SelloDeAceptacion`, la tarjeta de abajo (A-02). Estaba
 * repartido aquí sin nombre y con la mitad de los datos faltando; junto y
 * rotulado se lee como el registro que es.
 */

import { GrupoDeEvidencia } from '@/features/almacen/components/GrupoDeEvidencia'

interface UltimaEntregaProps {
  /** Etiqueta ya traducida del estado con el que se entregó. */
  estado: string | null
  descripcion: string | null
  /** Las que tomó quien entregó al registrar el movimiento. */
  fotos: string[]
  /**
   * Las que tomó quien recibió al aceptar o rechazar (A-01).
   *
   * Van aparte y rotuladas: en un rechazo son justo las que hay que comparar
   * contra las de la entrega, y mezcladas no se distingue cuál es cuál.
   */
  fotosDelReceptor: string[]
  onAbrirFoto: (fotos: string[], indice: number) => void
}

export const UltimaEntrega = ({
  estado,
  descripcion,
  fotos,
  fotosDelReceptor,
  onAbrirFoto,
}: UltimaEntregaProps) => {
  // El visor recorre una sola lista, en el mismo orden en que se pintan.
  const todas = [...fotos, ...fotosDelReceptor]

  return (
    <div
      className="bg-white rounded-xl border shadow-sm mb-6"
      style={{ borderColor: '#E0E0E0' }}
    >
      <div
        className="px-6 pt-5 pb-4"
        style={{ borderColor: '#E0E0E0', borderBottom: '1px solid #E0E0E0' }}
      >
        <h2 className="text-base font-semibold text-gray-800">
          Última evidencia de entrega
        </h2>
      </div>

      <div className="px-6 py-5">
        <p className="text-sm text-gray-500">
          Estado de entrega:{' '}
          <span className="text-gray-800 font-medium">{estado ?? '—'}</span>
        </p>

        <p className="text-sm text-gray-500 mt-3 mb-1">Descripción:</p>
        <p className="text-sm text-gray-800 whitespace-pre-line">
          {descripcion?.trim() || 'Sin descripción'}
        </p>
      </div>

      {todas.length > 0 && (
        <div
          className="px-6 py-5 space-y-6"
          style={{ borderColor: '#E0E0E0', borderTop: '1px solid #E0E0E0' }}
        >
          <GrupoDeEvidencia
            titulo="Fotos de quien entrega"
            urls={fotos}
            onVer={(url) => onAbrirFoto(todas, todas.indexOf(url))}
          />

          {fotosDelReceptor.length > 0 && (
            <GrupoDeEvidencia
              titulo="Fotos de quien recibe"
              urls={fotosDelReceptor}
              onVer={(url) => onAbrirFoto(todas, todas.indexOf(url))}
            />
          )}
        </div>
      )}
    </div>
  )
}
